/**
 * Custom hooks for company form state management
 * Consolidates repeated form logic patterns
 */

import { useState, useCallback, useMemo, useRef } from 'react'

/**
 * Hook to manage duplicate warning popover state
 */
export const useDuplicateWarning = () => {
  const [showPopover, setShowPopover] = useState(false)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setShowPopover(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPopover(false)
    }, 200)
  }, [])

  const cleanup = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
  }, [])

  return {
    showPopover,
    setShowPopover,
    handleMouseEnter,
    handleMouseLeave,
    cleanup,
  }
}

/**
 * Hook to manage selectable field state (category, branch type, employee size)
 */
export interface SelectableFieldState {
  value: string | number | null
  setValue: (value: string | number | null) => void
}

export const useSelectableField = (initialValue?: string | number | null): SelectableFieldState => {
  const [value, setValue] = useState<string | number | null>(initialValue || null)
  return useMemo(() => ({ value, setValue }), [value, setValue])
}

/**
 * Hook to manage form UI state
 */
export const useFormUIState = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const resetState = useCallback(() => {
    setIsSubmitting(false)
    setIsLoading(false)
  }, [])

  return { isSubmitting, setIsSubmitting, isLoading, setIsLoading, resetState }
}

/**
 * Hook to manage modal/form opening state
 */
export const useFormModalState = <T = unknown>() => {
  const [showForm, setShowForm] = useState(false)
  const [selectedItem, setSelectedItem] = useState<T | null>(null)

  const openForm = useCallback((item?: T) => {
    setSelectedItem(item || null)
    setShowForm(true)
  }, [])

  const closeForm = useCallback(() => {
    setShowForm(false)
    setSelectedItem(null)
  }, [])

  return { showForm, selectedItem, openForm, closeForm, setShowForm, setSelectedItem }
}

/**
 * Hook to manage multiple modal/form pairs (address, contact, etc.)
 */
export interface MultiFormModalState {
  activeFormType: string | null
  item: unknown
  showForm: boolean
}

export const useMultiFormModal = () => {
  const [state, setState] = useState<MultiFormModalState>({
    activeFormType: null,
    item: null,
    showForm: false,
  })

  const openForm = useCallback((formType: string, item?: unknown) => {
    setState({
      activeFormType: formType,
      item: item ?? null,
      showForm: true,
    })
  }, [])

  const closeForm = useCallback(() => {
    setState({
      activeFormType: null,
      item: null,
      showForm: false,
    })
  }, [])

  return { ...state, openForm, closeForm }
}

/**
 * Hook for managing collapsible form sections
 */
export const useCollapsibleSections = (initialState: Record<string, boolean> = {}) => {
  const [expandedSections, setExpandedSections] = useState(initialState)

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }, [])

  const expandAll = useCallback(() => {
    const allExpanded: Record<string, boolean> = {}
    Object.keys(expandedSections).forEach((key) => {
      allExpanded[key] = true
    })
    setExpandedSections(allExpanded)
  }, [expandedSections])

  const collapseAll = useCallback(() => {
    const allCollapsed: Record<string, boolean> = {}
    Object.keys(expandedSections).forEach((key) => {
      allCollapsed[key] = false
    })
    setExpandedSections(allCollapsed)
  }, [expandedSections])

  return { expandedSections, toggleSection, expandAll, collapseAll }
}
